import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { format, isValid, isBefore, isAfter, startOfDay } from "date-fns";
import { Formik, FormikErrors, FormikProps } from "formik";
import { ENGLISH_ABBREV, SPANISH_ABBREV } from "@bto/shared";
import { AlertTitle, MenuItem } from "@mui/material";
import { BannerInformationIcon } from "../Banner/BannerInformationIcon";
import { Button } from "../shared/Button/Button";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  CreateAccountButtonContainer,
  CreateAccountInput,
  CreateAccountLanguageSelect,
  CreateAccountLoginMessageContainer,
  CreateAccountCenterContainer,
  CreateAccountGuidanceBanner,
  CreateAccountStepContainer,
  CreateAccountHeadingGroup,
  Spinner,
  CreateAccountFormStyled,
  CreateAccountPassword,
  CreateAccountPasswordInfoContainer,
  FormFieldInput,
  ButtonContainer,
  CreateAccountPrivacyPolicy,
  CreateAccountMismatchMessage,
  MismatchErrorTitle,
  MismatchErrorBody,
  CreateAccountPasswordValidationContainer,
} from "./CreateAccountForm.styles";
import {
  CreateAccountRequest,
  SendSignUpErrorAction,
  PatientPhiVerifyRequest,
} from "./Services/CreateAccountService";
import { useSpanishSignupEnabled } from "./useSpanishSignupEnabled";
import { SignupVerificationValues, MIN_DATE_OF_BIRTH } from "./types";
import {
  LANDING_PAGE_URL,
  LOGIN_PAGE_URL,
  VERIFY_EMAIL_URL,
} from "../../utils/urlConstants";
import { createAccountInitialValues } from "./Services/CreateAccountServiceHelpers";
import { CreateAccountPasswordInfo } from "./CreateAccountPasswordInfo";
import { getPasswordRequirements } from "./passwordRequirements";
import { DateOfBirthInput } from "./DateOfBirthInput";
import loading from "../../assets/icons/loading.svg";
import { patientState } from "../../recoil/atoms";
import { UserAuthClientResponse } from "../../recoil/types";
import { validateEmail } from "../../utils/hooks/helpers";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { getUserAuth } from "../../recoil/selectors";
import { setCookieAccessToken } from "../../utils/requestUtils";
import {
  normalizeLanguage,
  persistLanguagePreference,
} from "../../i18n/languagePreference";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";

export const CreateAccountFormContent: React.FC<{
  setPathname: () => void;
}> = ({ setPathname }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { smsCode } = useParams<{ smsCode: string }>();
  const spanishEnabled = useSpanishSignupEnabled(smsCode);
  const isLanguageToggleEnabled = spanishEnabled === true;
  const auth = useRecoilValue(getUserAuth);
  const [isFromEmailLink, setIsFromEmailLink] = useState(false);
  const [isSetPassword, setIsSetPassword] = useState(false);
  const [isMismatchError, setIsMismatchError] = useState(false);

  useEffect(() => {
    const unlisten = history.listen(() => {
      setPathname();
    });
    return () => {
      unlisten();
    };
  }, []);

  useEffect(() => {
    if (!smsCode) {
      history.push(LOGIN_PAGE_URL);
    }
    if (auth) {
      history.push(LANDING_PAGE_URL);
    }
  }, [auth]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromEmail = params.get("e"); // e is set from a sign up email link
      setIsFromEmailLink(fromEmail === "True");
    } catch (e) {
      // from sms
      setIsFromEmailLink(false);
    }
  }, []);

  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const [, setPatientInfo] = useRecoilState(patientState);

  useEffect(() => {
    TrackAnalyticsEvent(AnalyticsEventNames.page_view, undefined, smsCode, {
      page_name: "SignupPage",
    });
  }, []);

  // The signup form's language dropdown drives the whole-form language (like the
  // in-app LanguageSwitcher) and the preference persisted at signup. It only
  // renders when the Spanish-comms flag is on, so the handler need not re-check it.
  const handleLanguageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const selectedLanguage = normalizeLanguage(event.target.value);
    i18n.changeLanguage(selectedLanguage);
    persistLanguagePreference(selectedLanguage);
  };

  const onSubmit = async ({
    first_name,
    last_name,
    dob,
    zip,
    email,
    password,
  }: SignupVerificationValues): Promise<void> => {
    if (submissionInFlight.current) return;
    submissionInFlight.current = true;
    setIsSubmitting(true);
    try {
      const response = await CreateAccountRequest({
        first_name,
        last_name,
        dob: dob ? format(dob, "yyyy-MM-dd") : "",
        zip,
        email,
        password,
        sms_code: smsCode,
        is_from_email: isFromEmailLink,
        // Persist Spanish only when the flag is explicitly enabled for this
        // requisition. While it's still pending (or disabled) default to English,
        // so a slow page-load can't let a non-targeted ?lang=es signup opt into
        // Spanish comms (the verification email and Customer.io trust this value).
        language: isLanguageToggleEnabled
          ? normalizeLanguage(i18n.language)
          : ENGLISH_ABBREV,
      });
      const statusCode: number = response[0];
      const newUserInfo: UserAuthClientResponse | undefined = response[1];
      if (newUserInfo?.auth0.access_token) {
        setCookieAccessToken(newUserInfo.auth0.access_token);
        setPatientInfo(newUserInfo);
        history.push(VERIFY_EMAIL_URL);
      } else {
        let msg = "";
        switch (statusCode) {
          case 400:
            msg = t("createAccount.errors.invitedDetailsMismatch");
            break;
          case 404:
            msg = t("createAccount.errors.unknownSignupLink");
            break;
          case 406:
            msg = t("createAccount.errors.expiredSignupLink");
            break;
          case 409:
            msg = t("createAccount.errors.accountCreation");
            break;
          case 429:
            msg = t("createAccount.errors.tooManyAttempts");
            break;
          case 412:
            msg = t("createAccount.errors.verifyPersonalDetails");
            break;
          case 500:
            msg = t("createAccount.errors.unknownRegistration");
            break;
          case 501:
            msg = t("createAccount.errors.network");
            break;
          default:
            break;
        }
        setErrorMessage(msg);
        SendSignUpErrorAction(smsCode, msg).catch(() => undefined);
      }
    } catch (e) {
      const msg = t("createAccount.errors.network");
      setErrorMessage(msg);
      TrackAnalyticsEvent(AnalyticsEventNames.interaction, undefined, smsCode, {
        interaction_type: "form_submission_failed",
        element_name: "signup_form",
        failure_reason: msg,
        page_name: "SignupPage",
      });
      SendSignUpErrorAction(smsCode, msg).catch(() => undefined);
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const onContinue = async ({
    first_name,
    last_name,
    email,
    dob,
    zip,
  }: SignupVerificationValues): Promise<void> => {
    if (submissionInFlight.current) return;
    submissionInFlight.current = true;
    setIsSubmitting(true);
    try {
      setIsMismatchError(false);

      const response = await PatientPhiVerifyRequest({
        first_name,
        last_name,
        dob: dob ? format(dob, "yyyy-MM-dd") : "",
        zip,
        email,
        sms_code: smsCode,
      });
      if (response?.status === 200) {
        history.push(LOGIN_PAGE_URL, {
          from: "createAccount",
          existingEmail: email,
        });
      } else if (response?.status === 202) {
        setIsSetPassword(true);
      } else if (response?.status === 429) {
        setErrorMessage(t("createAccount.errors.tooManyAttempts"));
      } else {
        setIsMismatchError(true);
        TrackAnalyticsEvent(
          AnalyticsEventNames.interaction,
          undefined,
          smsCode,
          {
            interaction_type: "form_submission_failed",
            element_name: "signup_form",
            failure_reason: "patient_info_mismatch",
            page_name: "SignupPage",
          },
        );
      }
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <CreateAccountCenterContainer data-testid="CreateAccountForm">
      <ErrorPopup message={errorMessage} hideMessage={setErrorMessage} onTop />
      <CreateAccountStepContainer $verificationStep={!isSetPassword}>
        <CreateAccountHeadingGroup data-testid="create-account-heading">
          {GetTypographyContent({
            content: t("createAccount.welcome"),
            size: "xlarge",
            textColor: "neutral700",
          })}
          {!isSetPassword
            ? GetTypographyContent({
                content: t("createAccount.title"),
                size: "h3",
                isBold: true,
                textColor: "neutral700",
                padding: "5px 0 0 0",
              })
            : GetTypographyContent({
                content: t("createAccount.password.title"),
                size: "h3",
                isBold: true,
                textColor: "neutral700",
                padding: "5px 0 0 0",
              })}
        </CreateAccountHeadingGroup>
        {smsCode && !isSetPassword && isLanguageToggleEnabled && (
          <CreateAccountLanguageSelect
            select
            variant="standard"
            name="language_preference"
            label={t("createAccount.fields.languagePreference")}
            value={normalizeLanguage(i18n.language)}
            onChange={handleLanguageChange}
          >
            {/* Language names are endonyms — never translated. */}
            <MenuItem value={ENGLISH_ABBREV}>English</MenuItem>
            <MenuItem value={SPANISH_ABBREV}>Español</MenuItem>
          </CreateAccountLanguageSelect>
        )}
        {smsCode && !isSetPassword && (
          <CreateAccountGuidanceBanner
            data-testid="create-account-guidance"
            icon={
              <span aria-hidden="true">
                <BannerInformationIcon />
              </span>
            }
            severity="info"
            role="region"
            aria-labelledby="create-account-guidance-title"
          >
            <AlertTitle
              role="heading"
              aria-level={2}
              id="create-account-guidance-title"
            >
              {t("createAccount.guidance.title")}
            </AlertTitle>
            <div>
              <p>{t("createAccount.guidance.bodyIntro")}</p>
              <p>{t("createAccount.guidance.bodyEmail")}</p>
            </div>
          </CreateAccountGuidanceBanner>
        )}
        {smsCode && (
          <Formik<SignupVerificationValues>
            initialValues={{
              ...createAccountInitialValues,
              dob: null,
              zip: "",
            }}
            onSubmit={(values) =>
              isSetPassword ? onSubmit(values) : onContinue(values)
            }
            validateOnChange={false}
            validate={(values) => {
              const errors: FormikErrors<SignupVerificationValues> = {};
              if (!values.first_name) {
                errors.first_name = t("createAccount.errors.required");
              }
              if (!values.email) {
                errors.email = t("createAccount.errors.required");
              }
              if (values.email && !validateEmail(values.email)) {
                errors.email = t("createAccount.errors.invalidEmail");
              }

              if (!values.last_name) {
                errors.last_name = t("createAccount.errors.required");
              }
              if (!values.zip) {
                errors.zip = t("createAccount.errors.required");
              } else if (!/^[0-9]{5}$/.test(values.zip)) {
                errors.zip = t("createAccount.errors.invalidZip");
              }
              if (
                values.dob &&
                (!isValid(values.dob) ||
                  isBefore(values.dob, MIN_DATE_OF_BIRTH) ||
                  isAfter(startOfDay(values.dob), startOfDay(new Date())))
              ) {
                errors.dob = t("createAccount.errors.invalidDate");
              } else if (!values.dob) {
                errors.dob = t("createAccount.errors.required");
              }

              if (isSetPassword && !values.password) {
                errors.password = t("createAccount.errors.required");
              } else if (
                isSetPassword &&
                !Object.values(getPasswordRequirements(values.password)).every(
                  Boolean,
                )
              ) {
                errors.password = t("createAccount.errors.passwordNotValid");
              }

              return errors;
            }}
          >
            {({
              values,
              errors,
              handleSubmit,
              setFieldValue,
              setFieldError,
            }: FormikProps<SignupVerificationValues>) => (
              <FormFieldInput>
                <CreateAccountFormStyled
                  isPasswordSection={isSetPassword}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !isSubmitting &&
                      e.target instanceof HTMLInputElement
                    ) {
                      handleSubmit();
                    }
                  }}
                >
                  {!isSetPassword ? (
                    <>
                      <CreateAccountInput
                        type="text"
                        name="first_name"
                        InputLabelProps={{ required: true }}
                        inputProps={{ "aria-required": true }}
                        value={values.first_name}
                        placeholder={t("createAccount.fields.firstName")}
                        label={t("createAccount.fields.firstName")}
                        onChange={(event) => {
                          setFieldError("first_name", "");
                          setFieldValue("first_name", event.target.value);
                          setIsMismatchError(false);
                        }}
                        onFocus={() => {
                          TrackAnalyticsEvent(
                            AnalyticsEventNames.interaction,
                            undefined,
                            smsCode,
                            {
                              interaction_type: "input_focus",
                              element_name: "first_name_input",
                              page_name: "SignupPage",
                            },
                          );
                        }}
                        error={errors.first_name}
                      />
                      <CreateAccountInput
                        type="text"
                        name="last_name"
                        InputLabelProps={{ required: true }}
                        inputProps={{ "aria-required": true }}
                        value={values.last_name}
                        placeholder={t("createAccount.fields.lastName")}
                        label={t("createAccount.fields.lastName")}
                        onChange={(event) => {
                          setFieldError("last_name", "");
                          setFieldValue("last_name", event.target.value);
                          setIsMismatchError(false);
                        }}
                        onFocus={() => {
                          TrackAnalyticsEvent(
                            AnalyticsEventNames.interaction,
                            undefined,
                            smsCode,
                            {
                              interaction_type: "input_focus",
                              element_name: "last_name_input",
                              page_name: "SignupPage",
                            },
                          );
                        }}
                        error={errors.last_name}
                      />
                      <DateOfBirthInput
                        value={values.dob}
                        onChange={(date) => {
                          setFieldError("dob", "");
                          setFieldValue("dob", date);
                          setIsMismatchError(false);
                        }}
                        onFocus={() => {
                          TrackAnalyticsEvent(
                            AnalyticsEventNames.interaction,
                            undefined,
                            smsCode,
                            {
                              interaction_type: "input_focus",
                              element_name: "dob_input",
                              page_name: "SignupPage",
                            },
                          );
                        }}
                        error={errors.dob}
                      />
                      <CreateAccountInput
                        type="text"
                        name="zip"
                        InputLabelProps={{ required: true }}
                        value={values.zip}
                        label={t("createAccount.fields.zipCode")}
                        placeholder={t("createAccount.fields.zipCode")}
                        inputProps={{
                          "aria-required": true,
                          inputMode: "numeric",
                          maxLength: 5,
                          autoComplete: "postal-code",
                        }}
                        onChange={(event) => {
                          setFieldError("zip", "");
                          setFieldValue("zip", event.target.value);
                          setIsMismatchError(false);
                        }}
                        error={errors.zip}
                      />
                      <CreateAccountInput
                        type="email"
                        name="email"
                        InputLabelProps={{ required: true }}
                        inputProps={{ "aria-required": true }}
                        value={values.email}
                        placeholder={t("createAccount.fields.email")}
                        label={t("createAccount.fields.email")}
                        onChange={(event) => {
                          setFieldError("email", "");
                          setFieldValue("email", event.target.value);
                          setIsMismatchError(false);
                        }}
                        onFocus={() => {
                          TrackAnalyticsEvent(
                            AnalyticsEventNames.interaction,
                            undefined,
                            smsCode,
                            {
                              interaction_type: "input_focus",
                              element_name: "email_input",
                              page_name: "SignupPage",
                            },
                          );
                        }}
                        error={errors.email}
                      />
                    </>
                  ) : (
                    <div />
                  )}

                  {isSetPassword ? (
                    <CreateAccountPasswordInfoContainer>
                      <CreateAccountPassword
                        type="password"
                        name="password"
                        value={values.password}
                        placeholder={t("createAccount.fields.password")}
                        onChange={(event) => {
                          setFieldError("password", "");

                          setFieldValue("password", event.target.value);
                        }}
                        label={t("createAccount.fields.password")}
                        size="small"
                        error={Boolean(errors.password)}
                        helperText={errors.password}
                      />

                      <CreateAccountPasswordValidationContainer>
                        <CreateAccountPasswordInfo
                          {...getPasswordRequirements(values.password)}
                        />
                      </CreateAccountPasswordValidationContainer>
                    </CreateAccountPasswordInfoContainer>
                  ) : null}
                  {isMismatchError && (
                    <CreateAccountMismatchMessage role="alert">
                      <MismatchErrorTitle>
                        {t("createAccount.mismatch.title")}
                      </MismatchErrorTitle>
                      <MismatchErrorBody>
                        {t("createAccount.mismatch.body")}
                      </MismatchErrorBody>
                      <MismatchErrorBody>
                        {t("createAccount.mismatch.supportPrefix")}{" "}
                        <a
                          href="tel:6504602554"
                          style={{ textDecoration: "underline" }}
                        >
                          650-460-2554
                        </a>
                      </MismatchErrorBody>
                    </CreateAccountMismatchMessage>
                  )}
                  {isSetPassword && (
                    <CreateAccountPrivacyPolicy>
                      <p>
                        {t("createAccount.legal.prefix")}
                        <a
                          style={{ textDecoration: "underline" }}
                          href="https://www.billiontoone.com/privacy-policy"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("createAccount.legal.privacyPolicy")}
                        </a>
                        {t("createAccount.legal.separator")}
                        <a
                          style={{ textDecoration: "underline" }}
                          href="https://www.billiontoone.com/privacy-practices"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("createAccount.legal.noticeOfPrivacyPractices")}
                        </a>
                        {t("createAccount.legal.finalSeparator")}
                        <a
                          style={{ textDecoration: "underline" }}
                          href="../../client/terms_of_use"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("createAccount.legal.termsOfUse")}
                        </a>
                      </p>
                    </CreateAccountPrivacyPolicy>
                  )}
                </CreateAccountFormStyled>
                <CreateAccountButtonContainer>
                  {smsCode && isSubmitting && (
                    <Spinner>
                      <img src={loading} alt="Loading" />
                    </Spinner>
                  )}
                  <ButtonContainer>
                    {isSetPassword ? (
                      <Button
                        disabled={isSubmitting}
                        type="submit"
                        label={t("createAccount.buttons.createAccount")}
                        size="medium"
                        onClick={() => handleSubmit()}
                      />
                    ) : (
                      <Button
                        disabled={isSubmitting}
                        type="submit"
                        label={t("createAccount.buttons.continue")}
                        size="medium"
                        onClick={() => handleSubmit()}
                      />
                    )}
                  </ButtonContainer>
                </CreateAccountButtonContainer>
                {!isSetPassword && (
                  <CreateAccountLoginMessageContainer>
                    {GetTypographyContent({
                      content: t("createAccount.alreadyHaveAccount"),
                      size: "medium",
                      textColor: "neutral700",
                    })}
                    {GetTypographyContent({
                      content: t("createAccount.login"),
                      size: "medium",
                      textColor: "greenDark",
                      link: LOGIN_PAGE_URL,
                    })}
                  </CreateAccountLoginMessageContainer>
                )}
              </FormFieldInput>
            )}
          </Formik>
        )}
      </CreateAccountStepContainer>
    </CreateAccountCenterContainer>
  );
};
